export function deepClone(value) {
  if (value === undefined || value === null) return value;

  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value));
}

export function isCabinetDefinition(definition) {
  return String(definition?.category || "")
    .toLowerCase()
    .includes("cabinet");
}

export function getDefinitionForNode(node, definitions = []) {
  return definitions.find(
    (d) => String(d.id) === String(node?.definitionId)
  );
}

function getNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getNodeParameterNumber(node, definition, key, fallback) {
  return (
    getNumber(node?.parameterOverrides?.[key], NaN) ||
    getNumber(definition?.defaultParameters?.[key], NaN) ||
    fallback
  );
}

export function calculateNextCabinetPosition({
  definition,
  overrides = {},
  nodes = [],
  definitions = [],
  selectedId = null,
}) {
  if (!isCabinetDefinition(definition) || !nodes.length) {
    return { x: 0, z: 0, rotationY: 0 };
  }

  const cabinetNodes = nodes.filter((node) => {
    const def = getDefinitionForNode(node, definitions);
    return isCabinetDefinition(def);
  });

  if (!cabinetNodes.length) {
    return { x: 0, z: 0, rotationY: 0 };
  }

  let baseNode = cabinetNodes[cabinetNodes.length - 1];

  if (selectedId) {
    const selectedCabinet = cabinetNodes.find(
      (node) => String(node.id) === String(selectedId)
    );

    if (selectedCabinet) {
      baseNode = selectedCabinet;
    }
  }

  const baseDefinition = getDefinitionForNode(baseNode, definitions);

  const baseWidth = getNodeParameterNumber(
    baseNode,
    baseDefinition,
    "width",
    600
  );

  const nextWidth =
    getNumber(overrides.width, NaN) ||
    getNumber(definition?.defaultParameters?.width, NaN) ||
    600;

  const baseX = getNumber(baseNode.transform?.position?.x, 0);
  const baseZ = getNumber(baseNode.transform?.position?.z, 0);
  const rotationY = getNumber(baseNode.transform?.rotation?.y, 0);

  const rad = rotationY * (Math.PI / 180);
  const distance = baseWidth / 2 + nextWidth / 2;

  const dx = Math.cos(rad) * distance;
  const dz = -Math.sin(rad) * distance;

  return {
    x: Number((baseX + dx).toFixed(3)),
    z: Number((baseZ + dz).toFixed(3)),
    rotationY,
  };
}

export function buildQuickAddCreationOptions({
  direction,
  options = {},
  selectedNode,
  resolvedNode,
}) {
  if (!selectedNode || !resolvedNode) return null;

  const baseWidth = getNumber(resolvedNode.effectiveParameters?.width, 600);
  const baseDepth = getNumber(resolvedNode.effectiveParameters?.depth, 560);

  const nextWidth = getNumber(options.width, baseWidth);
  const nextDepth = getNumber(options.depth, baseDepth);

  const rotationY = getNumber(selectedNode.transform?.rotation?.y, 0);

  const nextRotationY =
    direction === "cornerRight"
      ? rotationY + 90
      : direction === "cornerLeft"
      ? rotationY - 90
      : rotationY;

  const effectiveDirection =
    direction === "cornerRight" || direction === "cornerLeft"
      ? "front"
      : direction;

  const rad = rotationY * (Math.PI / 180);

  const localDirections = {
    right: {
      x: 1,
      z: 0,
      distance: baseWidth / 2 + nextWidth / 2,
    },
    left: {
      x: -1,
      z: 0,
      distance: baseWidth / 2 + nextWidth / 2,
    },
    front: {
      x: 0,
      z: 1,
      distance: baseDepth / 2 + nextDepth / 2,
    },
    back: {
      x: 0,
      z: -1,
      distance: baseDepth / 2 + nextDepth / 2,
    },
  };

  const dir = localDirections[effectiveDirection];
  if (!dir) return null;

  const localX = dir.x * dir.distance;
  const localZ = dir.z * dir.distance;

  const worldDx = localX * Math.cos(rad) + localZ * Math.sin(rad);
  const worldDz = -localX * Math.sin(rad) + localZ * Math.cos(rad);

  const baseX = getNumber(selectedNode.transform?.position?.x, 0);
  const baseZ = getNumber(selectedNode.transform?.position?.z, 0);

  return {
    parameterOverrides: {
      ...(selectedNode.parameterOverrides || {}),
      width: nextWidth,
      height: getNumber(resolvedNode.effectiveParameters?.height, 720),
      depth: nextDepth,
      floorOffset: getNumber(resolvedNode.effectiveParameters?.floorOffset, 0),
    },

    partitions: selectedNode.partitions
      ? deepClone(selectedNode.partitions)
      : undefined,

    materialAssignments: selectedNode.materialAssignments
      ? deepClone(selectedNode.materialAssignments)
      : undefined,

    childDefinitionOverrides: selectedNode.childDefinitionOverrides
      ? deepClone(selectedNode.childDefinitionOverrides)
      : undefined,

    parentChildDefinitionOverrides: selectedNode.parentChildDefinitionOverrides
      ? deepClone(selectedNode.parentChildDefinitionOverrides)
      : undefined,

    shelfOverrides: selectedNode.shelfOverrides
      ? deepClone(selectedNode.shelfOverrides)
      : undefined,

    transform: {
      position: {
        x: Number((baseX + worldDx).toFixed(3)),
        z: Number((baseZ + worldDz).toFixed(3)),
      },
      rotation: {
        y: nextRotationY,
      },
    },
  };
}